import { IPromise, IRootScopeService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IIndicator, IUIError } from "@gtmhub/error-handling";
import { BadgeEvents } from "../badgeEvents";
import { IGetAnnouncementsParams } from "../models/announcements";
import { IBadge, IBadgeAssignment } from "../models/badges";
import { AnnouncementService } from "../services/announcements.service";
import { BadgeService } from "../services/badges";
import { Ng1ConfirmBadgeActionService } from "../services/ng1-confirm-badge-action.service";

interface IBadgeScope extends IModalScope {
  badgeAssignment: IBadgeAssignment;
  badge: IBadge;
  indicators: { gettingBadgeAssignment?: IIndicator };
  unassign(): void;
  approveBadgeAward(badgeAssignment: IBadgeAssignment): void;
  rejectBadgeAward(badgeAssignment: IBadgeAssignment): void;
}

export class BadgeCtrl {
  public static $inject = ["$rootScope", "$scope", "BadgeService", "AnnouncementService", "Ng1ConfirmBadgeActionService", "badgeAssignmentId"];

  constructor(
    private $rootScope: IRootScopeService & ITraceRootScopeService,
    private $scope: IBadgeScope,
    private badgeService: BadgeService,
    private announcementService: AnnouncementService,
    private confirmBadgeActionService: Ng1ConfirmBadgeActionService,
    private badgeAssignmentId: string
  ) {
    this.badgeEvents = new BadgeEvents(this.$scope);

    this.$scope.indicators = {};
    this.$scope.unassign = this.unassign;
    this.$scope.approveBadgeAward = this.approveBadgeAward;
    this.$scope.rejectBadgeAward = this.rejectBadgeAward;

    this.init();
  }

  private init = (): IPromise<unknown> => {
    return this.getBadgeAssignment();
  };

  private badgeEvents: BadgeEvents;

  private approveBadgeAward = (badgeAssignment: IBadgeAssignment) => {
    this.confirmBadgeActionService.confirmApprove({
      action: () =>
        Promise.resolve(this.badgeService.approveBadgeAward(badgeAssignment.id)).then(() => {
          this.badgeEvents.broadcastBadgeAwardChanged({ action: "assigned", assignment: badgeAssignment, badge: this.$scope.badge });

          const announcement = this.badgeService.configureRewardAnnouncement(badgeAssignment);
          this.announcementService.post(announcement);
          this.getBadgeAssignment();
        }),
    });
  };

  private rejectBadgeAward = (badgeAssignment: IBadgeAssignment) => {
    this.confirmBadgeActionService.confirmReject({
      action: () => Promise.resolve(this.badgeService.rejectBadgeAward(badgeAssignment.id)),
    });
  };

  private unassign = () => {
    this.confirmBadgeActionService.confirmUnassign({
      action: () =>
        Promise.resolve(
          this.$rootScope.traceAction("unassign_badge_award", () => {
            return this.badgeService
              .unassignBadge(this.badgeAssignmentId)
              .then(() => {
                this.$rootScope.$broadcast("employeeChanged", true);
                this.$rootScope.$broadcast("employeesChanged", true);
                this.badgeEvents.broadcastBadgeAwardChanged({
                  action: "unassigned",
                  assignment: this.$scope.badgeAssignment,
                  badge: this.$scope.badge,
                });

                const params: IGetAnnouncementsParams = {
                  skip: 0,
                  take: null,
                  authorId: this.$scope.badgeAssignment.assignedById,
                  before: null,
                  after: null,
                  fields: "id,authorId,announcementTargetId",
                  announcementType: "Recognition",
                };

                return this.announcementService.get(params).then((announcements) => {
                  if (announcements.items.length === 0) {
                    return;
                  }

                  for (let i = 0; i < announcements.items.length; i++) {
                    if (announcements.items[i].announcementTargetId === this.$scope.badgeAssignment.id) {
                      return this.announcementService.delete(announcements.items[i].id);
                    }
                  }
                });
              })
              .then(() => {
                this.$scope.$close();
              });
          })
        ),
    });
  };

  private getBadgeAssignment = (): IPromise<unknown> => {
    this.$scope.indicators.gettingBadgeAssignment = { progress: true };

    return this.badgeService
      .getBadgeAssignments([this.badgeAssignmentId], [], null)
      .then((assignments) => {
        if (assignments.totalCount === 0) {
          const error: IUIError = {
            body: "We cannot find the badge award.",
            showIntercomLink: true,
          };
          throw error;
        }

        this.$scope.badgeAssignment = assignments.items[0];

        return this.badgeService.getBadge(assignments.items[0].badgeId).then((badge) => {
          this.$scope.badge = badge;
          delete this.$scope.indicators.gettingBadgeAssignment;
        });
      })
      .catch((error) => {
        this.$scope.indicators.gettingBadgeAssignment = { error };
      });
  };
}
